import React, { useEffect, useMemo } from "react";
import {
  useTable,
  useRowSelect,
  usePagination,
  useExpanded,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import SelectInput from "../FormFields/SelectInput";
import { GlobalSearch } from "./GlobalSearch";

const CustomTable = ({
  data,
  columns,
  showCheckboxes = false,
  onCheckboxSelect,
  centerAlignedColumns = [],
  pagesToShow = 5,
  renderTools = () => {},
  showPageSizeChange = false,
  showPagination = false,
  selectedCheckboxes = [],
  isGlobalSearchEnable = false,
  globalSearchPosition = {},
  featureCss = "",
  initialCheckboxesData = []
}) => {
  const propData = useMemo(() => data, [data]);
  const propColumn = useMemo(() => columns, [columns]);
  const initialCheckboxes = useMemo(() => initialCheckboxesData, [initialCheckboxesData]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    getSubRows,
    selectedFlatRows,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    pageChange,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize },
    state,
  } = useTable(
    {
      columns: propColumn,
      data: propData,
      initialState: {
        selectedRowIds:
          initialCheckboxes?.reduce((obj, item) => {
            obj[item] = true;
            return obj;
          }, {}) || {},
      },
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (showCheckboxes) {
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <input
                type="checkbox"
                {...getToggleAllRowsSelectedProps()}
                style={{ cursor: "pointer" }}
                checked={
                  getToggleAllRowsSelectedProps().checked ||
                  (selectedCheckboxes?.length &&
                    selectedCheckboxes?.length === data?.length)
                }
              />
            ),
            Cell: ({ row }) => (
              <input
                type="checkbox"
                {...row.getToggleRowSelectedProps()}
                style={{ cursor: "pointer" }}
                checked={
                  row.getToggleRowSelectedProps()?.checked ||
                  selectedCheckboxes.includes(row.original.id)
                }
              />
            ),
          },
          ...columns,
        ]);
      }
    }
  );

  useEffect(() => {
    if (onCheckboxSelect) {
      onCheckboxSelect(selectedFlatRows);
    }
  }, [selectedFlatRows, onCheckboxSelect]);

  const calculatePageRange = () => {
    const totalPages = Math.ceil(data.length / pageSize);
    const currentPage = pageIndex + 1;
    const half = Math.floor(pagesToShow / 2);

    let start = currentPage - half;
    if (start < 1) start = 1;

    let end = start + pagesToShow - 1;
    if (end > totalPages) {
      end = totalPages;
      start = end - pagesToShow + 1;
      if (start < 1) start = 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handlePaginationChange = (newPage) => {
    pageChange(newPage);
  };

  const { globalFilter } = state;

  return (
    <div>
      <div className={`flex items-center justify-between mb-5 relative ${featureCss}`}>
        {isGlobalSearchEnable && (
          <div style={globalSearchPosition}>
            <GlobalSearch filter={globalFilter} setFilter={setGlobalFilter} />
          </div>
        )}
      </div>
      <table
        {...getTableProps()}
        className="custom-table font-size-small font-family-medium"
      >
        <thead>
          {headerGroups?.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers?.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={`table-header ${
                    centerAlignedColumns.includes(column.id)
                      ? "center-aligned"
                      : ""
                  }`}
                >
                  {column.render("Header")}
                  {/* Add a sort indicator */}
                  {column.id !== "selection" && (
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " ↓ "
                          : " ↑ "
                        : " ↓ "}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="bg-[var(--whiteColor)]">
          {data?.length === 0 ? (
            <tr>
              <td
                colSpan={showCheckboxes ? columns.length + 1 : columns.length}
                style={{ textAlign: "center" }}
              >
                No data to show
              </td>
            </tr>
          ) : (
            rows?.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells?.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className={`table-cell ${
                        centerAlignedColumns.includes(cell.column.id)
                          ? "center-aligned"
                          : ""
                      }`}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {showPagination && (
        <div className="pagination">
          <button
            onClick={() => handlePaginationChange(pageIndex - 1)}
            disabled={!canPreviousPage}
          >
            Previous
          </button>

          {calculatePageRange()?.map((pageNumber) => (
            <button
              key={pageNumber}
              onClick={() => handlePaginationChange(pageNumber - 1)}
              className={pageNumber === pageIndex + 1 ? "active" : ""}
            >
              {pageNumber}
            </button>
          ))}

          <button
            onClick={() => handlePaginationChange(pageIndex + 1)}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomTable;